import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Skeleton, Paper } from '@mui/material';
import { getPublicTimeline, getOnlineUsersCount } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatRelativeTime } from '../utils/dateUtils';
import ActivityReactions from '../components/common/ActivityReactions';
import { formatReviewText } from '../utils/formatText';
import { Helmet } from 'react-helmet-async';
import PublicIcon from '@mui/icons-material/Public';
import { useUser } from '../contexts/UserContext';

interface TimelineActivity {
  id: number;
  type: number;
  user: {
    user_id: number;
    username: string | null;
    display_name: string;
    avatar: string | null;
  };
  game: {
    id: number;
    name: string;
    cover_image: string | null;
  };
  created_at: string;
  old_status?: number;
  new_status?: number;
  review_text?: string;
  reactions?: { [key: number]: number };
  user_reaction?: number | null;
}

const getActionText = (type: number, oldStatus?: number, newStatus?: number) => {
  if (type === 1) {
    switch (newStatus) {
      case 1: return 'プレイ済みに追加';
      case 2: return 'プレイを開始';
      case 3: return null; // 中断への追加は表示しない
      case 4: return 'ゲームを積んだ';
      case 5: return 'ウィッシュリストに追加';
      default: return '';
    }
  }

  if (type === 2) {
    const getStatusText = (status: number) => {
      switch (status) {
        case 1: return 'プレイ済み';
        case 2: return 'プレイ中';
        case 3: return '中断';
        case 4: return '積みゲー';
        case 5: return 'ウィッシュリスト';
        default: return '';
      }
    };

    if (oldStatus === 1 && newStatus === 2) {
      return 'プレイ済みタイトルのプレイを再開';
    }
    if (oldStatus === 2 && newStatus === 1) {
      return 'プレイを終えた';
    }
    if (oldStatus === 2 && newStatus === 3) {
      return 'プレイを中断';
    }
    if (oldStatus === 3 && newStatus === 2) {
      return '中断していたタイトルのプレイを再開';
    }
    if (oldStatus === 4 && newStatus === 1) {
      return '積みゲーを消化';
    }
    if (oldStatus === 4 && newStatus === 2) {
      return '積みゲーのプレイを開始';
    }
    if (oldStatus === 5 && newStatus === 2) {
      return 'ウィッシュリストのゲームを開始';
    }
    if (oldStatus === 5 && newStatus === 4) {
      return 'ウィッシュリストのゲームを積んだ';
    }
    return `${getStatusText(oldStatus || 0)}から${getStatusText(newStatus || 0)}に変更`;
  }

  if (type === 3) return 'レビューを投稿';
  if (type === 4) return 'レビューを更新';

  return null;
};

const TimelineActivitySkeleton: React.FC = () => (
  <Box
    sx={{
      p: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(8px)',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Skeleton
        variant="text"
        width={200}
        sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
      />
      <Skeleton
        variant="text"
        width={60}
        sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
      />
    </Box>
    <Skeleton
      variant="rectangular"
      height={96}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1
      }}
    />
  </Box>
);

const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

// セッションストレージのキー
const STORAGE_KEY = 'publicTimeline';

const PublicTimelinePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [activities, setActivities] = useState<TimelineActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [onlineCount, setOnlineCount] = useState<number | null>(null);

  // 自分の投稿を除外
  const filteredActivities = useMemo(() => {
    if (!user) return activities;
    return activities.filter(activity => activity.user.user_id !== user.user_id);
  }, [activities, user]);

  const fetchTimeline = async () => {
    // セッションストレージからキャッシュを確認
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) {
      const { data, scrollPosition, cachedAt: storedCachedAt } = JSON.parse(cached);
      setActivities(data);
      setCachedAt(storedCachedAt);
      setLoading(false);
      // スクロール位置を復元
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
      return;
    }

    setLoading(true);
    try {
      const data = await getPublicTimeline();
      setActivities(data.results);
      setCachedAt(data.cached_at || null);
    } catch (error) {
      console.error('タイムラインの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  // ゲームページへ遷移する際にスクロール位置とデータを保存
  const handleGameClick = (gameId: number) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      data: activities,
      scrollPosition: window.scrollY,
      cachedAt
    }));
    navigate(`/game/${gameId}`);
  };

  // ページを離れるときにキャッシュをクリア（戻るボタン以外）
  useEffect(() => {
    return () => {
      // 他のページに遷移したらキャッシュをクリア
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/game/')) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };
  }, [location]);

  const handleReactionUpdate = (activityId: number, newReactions: { [key: number]: number }, newUserReaction: number | null) => {
    setActivities(prevActivities =>
      prevActivities.map(activity =>
        activity.id === activityId
          ? { ...activity, reactions: newReactions, user_reaction: newUserReaction }
          : activity
      )
    );
  };

  useEffect(() => {
    fetchTimeline();
    // オンラインユーザー数を取得
    getOnlineUsersCount()
      .then(data => setOnlineCount(data.count))
      .catch(err => console.error('オンラインユーザー数の取得に失敗:', err));
  }, []);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Helmet>
          <title>みんなのアクティビティ | PULSE Database</title>
        </Helmet>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <PublicIcon sx={{ color: 'text.main' }} />
          <Typography variant="h5" fontWeight="bold">
            グローバルアクティビティ
          </Typography>
        </Box>
        <Grid container spacing={1}>
          {[...Array(12)].map((_, index) => (
            <Grid item xs={12} key={index}>
              <TimelineActivitySkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Helmet>
        <title>みんなのアクティビティ | PULSE Database</title>
        <meta name="description" content="PULSE Databaseユーザーの最新アクティビティをチェック" />
      </Helmet>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <PublicIcon sx={{ color: 'text.main' }} />
        <Typography variant="h5" fontWeight="bold">
          グローバルアクティビティ
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          24時間以内のユーザーアクティビティがランダムに表示されます。
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
          {/* {onlineCount !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  boxShadow: '0 0 4px #4caf50',
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {onlineCount.toLocaleString()}人が記録 (24h)
              </Typography>
            </Box>
          )} */}
          {cachedAt && (
            <Typography variant="caption" color="text.secondary">
              最終更新: {formatRelativeTime(cachedAt)}
            </Typography>
          )}
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          {filteredActivities.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <Box sx={{
                p: 4,
                textAlign: 'center',
              }}>
                <Typography variant="body1" color="text.secondary">
                  現在表示できるアクティビティがありません。
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={1}>
              {filteredActivities.map((activity) => {
                const actionText = getActionText(activity.type, activity.old_status, activity.new_status);

                if (actionText === null) return null;

                return (
                  <Grid item xs={12} key={activity.id}>
                    <Box
                      sx={{
                        px: { xs: 1.5, md: 2 },
                        py: { xs: 1, md: 2 },
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(8px)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.5, md: 1 } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            lineHeight: 1.2,
                            color: 'text.secondary',
                          }}
                        >
                          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            {activity.user.display_name}
                          </Box>
                          が{actionText}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', lineHeight: 1.2, ml: 1, flexShrink: 0 }}
                        >
                          {formatRelativeTime(activity.created_at)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleGameClick(activity.game.id)}
                      >
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {activity.game.cover_image && (
                            <img
                              src={getCoverUrl(activity.game.cover_image)}
                              alt={activity.game.name}
                              style={{
                                width: 60,
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: 2
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">
                              {activity.game.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {activity.review_text && (
                        <Box
                          sx={{
                            mt: 1,
                            color: 'text.secondary',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            lineHeight: 1.2
                          }}
                        >
                          {formatReviewText(activity.review_text, true)}
                        </Box>
                      )}

                      <ActivityReactions
                        activityId={activity.id}
                        reactions={activity.reactions || {}}
                        userReaction={activity.user_reaction || null}
                        isPublic={true}
                        onReactionUpdate={handleReactionUpdate}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PublicTimelinePage;
